/**
 * The three decisions the visit list makes about a day, as plain functions.
 *
 * They live here rather than inside `VisitListPage` because they are the part
 * that can be wrong without looking wrong: a stylist reads the chips as counts
 * of her day, and steps through the drawer expecting to stay inside what she
 * filtered to. Both are arithmetic over a list, and neither needs a browser to
 * check.
 */

interface HasActivity {
  readonly sessionStatus: string
  /** Read only against `roomless`: the types with no fitting room — a pickup. */
  readonly tierKey?: string
}

interface HasId {
  readonly id: string
}

/**
 * The activity chips are OR, not AND.
 *
 * Pressing *List sent* and *Link open* shows both — a stylist pressing a
 * second chip is widening what she is looking at, not asking for an
 * appointment that is somehow in two states at once, which is nothing. No
 * chip pressed means the whole day.
 */
export function filterByActivity<T extends HasActivity>(
  visits: readonly T[],
  tones: readonly string[],
  roomless: ReadonlySet<string> = new Set(),
): readonly T[] {
  return tones.length === 0 ? visits : visits.filter((visit) => !roomless.has(visit.tierKey ?? '') && tones.includes(visit.sessionStatus))
}

/**
 * How many appointments of each activity the DAY holds — never the current
 * filter.
 *
 * A count that answered "within what is on screen" would change the number on
 * one chip when you pressed the chip beside it, and then no combination of
 * them reads as trustworthy.
 */
export function activityCounts(visits: readonly HasActivity[], roomless: ReadonlySet<string> = new Set()): Map<string, number> {
  const counts = new Map<string, number>()
  for (const visit of visits) {
    if (!roomless.has(visit.tierKey ?? '')) counts.set(visit.sessionStatus, (counts.get(visit.sessionStatus) ?? 0) + 1)
  }
  return counts
}

/**
 * Where the open appointment sits in the list the drawer is walking, and what
 * the arrows should do.
 *
 * The list passed in is whatever is on screen behind the drawer. Stepping into
 * an appointment the filter removed would leave her reading a row she cannot
 * see, with no way to tell why the list behind it is shorter than her walk.
 */
export function walkNeighbours<T extends HasId>(
  walk: readonly T[],
  openId: string | null | undefined,
): { index: number; current?: T; previous?: T; next?: T } {
  const index = openId ? walk.findIndex((visit) => visit.id === openId) : -1
  if (index < 0) return { index: -1 }
  return {
    index,
    ...(walk[index] ? { current: walk[index] } : {}),
    ...(index > 0 && walk[index - 1] ? { previous: walk[index - 1] as T } : {}),
    ...(index < walk.length - 1 && walk[index + 1] ? { next: walk[index + 1] as T } : {}),
  }
}
